import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, MapPin, Phone, Mail } from 'lucide-react';
import axios from 'axios';

/* ═══════════════════════════════════════════
   EDITABLE: Contact info
═══════════════════════════════════════════ */
const CONTACT_PHONE = '+91 9870281491';
const CONTACT_EMAIL = 'ashwamedh360@gmail.com';
const CONTACT_LOCATION = 'Vastu Central, Dadar West, Mumbai.';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EnquirySection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, formData);
    } catch (err) {
      // Still show success to user
    }
    setLoading(false);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <section id="contact" data-testid="enquiry-section" className="py-20 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#0A192F] mb-8">
              Enquiry Form
            </h2>

            {submitted ? (
              <div className="bg-white rounded-xl p-10 text-center shadow-md" data-testid="enquiry-success-msg">
                <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#0A192F] mb-2">Thank You!</h3>
                <p className="text-sm text-[#49566B] font-['Outfit']">We'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="enquiry-form" className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-[#0A192F] uppercase tracking-wider mb-2 font-['Outfit']">
                    Name *
                  </label>
                  <input
                    data-testid="enquiry-name-input"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-[#0A192F] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 transition-all font-['Outfit']"
                    placeholder="Your full name"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#0A192F] uppercase tracking-wider mb-2 font-['Outfit']">
                      Email *
                    </label>
                    <input
                      data-testid="enquiry-email-input"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-[#0A192F] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 transition-all font-['Outfit']"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#0A192F] uppercase tracking-wider mb-2 font-['Outfit']">
                      Phone No
                    </label>
                    <input
                      data-testid="enquiry-phone-input"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-[#0A192F] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 transition-all font-['Outfit']"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#0A192F] uppercase tracking-wider mb-2 font-['Outfit']">
                    Message
                  </label>
                  <textarea
                    data-testid="enquiry-message-input"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-[#0A192F] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 transition-all resize-none font-['Outfit']"
                    placeholder="How can we help you?"
                  />
                </div>
                <button
                  data-testid="enquiry-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3.5 bg-[#0A192F] text-[#D4AF37] font-semibold rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 border border-[#D4AF37]/30 disabled:opacity-60"
                >
                  {loading ? 'Sending...' : 'Send'} <Send size={16} />
                </button>
              </form>
            )}
          </motion.div>

          {/* Map + Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-6"
          >
            {/* Map - zoomed out by 5% (z=15 instead of z=17) */}
            <div className="rounded-xl overflow-hidden shadow-lg flex-1 min-h-[300px]" data-testid="google-map-embed">
              <iframe
                src="https://maps.google.com/maps?q=ASHWAMEDH+Financial+Services+Dadar+West+Mumbai+400028&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '300px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="ASHWAMEDH Financial Services Location"
              />
            </div>

            {/* Contact Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 shadow-sm flex flex-col items-center text-center gap-2">
                <MapPin size={22} className="text-[#D4AF37]" />
                <span className="text-xs text-[#0A192F] font-semibold font-['Outfit']">{CONTACT_LOCATION}</span>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm flex flex-col items-center text-center gap-2">
                <Phone size={22} className="text-[#D4AF37]" />
                <span className="text-xs text-[#0A192F] font-semibold font-['Outfit']">{CONTACT_PHONE}</span>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm flex flex-col items-center text-center gap-2">
                <Mail size={22} className="text-[#D4AF37]" />
                <span className="text-xs text-[#0A192F] font-semibold font-['Outfit']">{CONTACT_EMAIL}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
